import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ProductCard from "@/components/feature/ProductCard";
import SmartImage from "@/components/base/SmartImage";
import { categories, getProductsByCategory } from "@/mocks/products";

const heroImgs: Record<string, string> = {
  outerwear: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/9d1bccda-0335-4f01-9a13-ff933ab616a9_compressed_home-category-outerwear-charcoal-web.webp",
  footwear: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/8da94d55-03d8-4925-a3e3-9cdeb1aa7597_compressed_home-category-footwear-web.webp",
  tops: "https://readdy.ai/api/search-image?query=Editorial%20close-up%20detail%20photograph%20of%20layered%20outdoor%20technical%20clothing%20only%2C%20recycled%20moss%20green%20grid%20fleece%20half%20zip%20midlayer%20worn%20over%20a%20charcoal%20merino%20base%20layer%2C%20framed%20from%20chest%20to%20waist%20with%20absolutely%20no%20face%20and%20no%20head%20visible%20head%20fully%20cropped%20above%20frame%2C%20focus%20on%20fabric%20texture%20zipper%20pull%20chest%20pocket%20stitching%20and%20cuff%20detail%2C%20soft%20cool%20natural%20alpine%20morning%20light%2C%20muted%20forest%20and%20stone%20background%20softly%20out%20of%20focus%2C%20uncontrasty%20film%20photography%20look%2C%20quiet%20northvale%20outdoor%20brand%20aesthetic%2C%20clothing%20fills%20the%20frame%2C%20room%20for%20text%20at%20bottom&width=1600&height=900&seq=tops-hero-northvale-01&orientation=landscape&nocache=true",

  bottoms: "https://readdy.ai/api/search-image?query=Editorial%20extreme%20close-up%20detail%20photograph%20of%20technical%20outdoor%20trail%20pants%20fabric%20only%2C%20charcoal%20grey%20softshell%20four-way%20stretch%20woven%20textile%20with%20subtle%20diagonal%20weave%20texture%2C%20showing%20thigh%20and%20knee%20area%20seam%20stitching%20articulated%20knee%20dart%20and%20side%20cargo%20pocket%20zipper%20detail%2C%20absolutely%20no%20face%20no%20head%20no%20torso%20only%20pants%20fabric%20filling%20the%20frame%2C%20soft%20cool%20natural%20alpine%20morning%20light%2C%20muted%20forest%20and%20stone%20background%20softly%20out%20of%20focus%2C%20uncontrasty%20film%20photography%20look%2C%20quiet%20northvale%20outdoor%20brand%20aesthetic%2C%20fabric%20texture%20fills%20the%20frame%2C%20room%20for%20text%20at%20bottom&width=1600&height=900&seq=bottoms-hero-northvale-01&orientation=landscape",
  packs: "https://storage.helloreaddy.io/project_files/e02ced6d-98bc-4ba1-a643-ca5b440d11d8/0096c029-8100-4c56-a695-85d8e16ec4a2_compressed_home-category-packs-detail-web.webp",
};

const intros: Record<string, string> = {
  outerwear: "Weather-first shells and technical jackets, built for shoulder-season alpine.",
  tops: "Quiet midlayers and next-to-skin pieces, engineered for long days outside.",
  bottoms: "Trail pants and softshell layers, cut for real movement.",
  footwear: "Trail shoes and fast-hike footwear, built for wet rock and long miles.",
  packs: "Technical daypacks and alpine carry, disciplined enough to disappear on your back.",
  accessories: "The small things you actually end up wearing.",
};

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  if (!cat) return <Navigate to="/shop" replace />;
  const list = getProductsByCategory(slug);
  const hero = heroImgs[slug];

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="solid" />
      <main className="flex-1 pt-[72px] md:pt-20">
        <section className="relative w-full h-[260px] md:h-[420px] overflow-hidden bg-background-100">
          {hero && (
            <>
              <SmartImage src={hero} alt={cat.name} tone="dark" eager imgClassName={`absolute inset-0 w-full h-full object-cover ${slug === "footwear" ? "object-[center_75%]" : "object-top"}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/25"></div>
            </>
          )}
          <div className="relative z-10 h-full w-full px-5 md:px-10 pb-8 md:pb-10 flex flex-col justify-end">
            <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-100/80 mb-3">
              <Link to="/shop" className="hover:text-foreground-50 cursor-pointer">Shop</Link> / {cat.name}
            </p>
            <h1 className="text-[32px] md:text-[44px] leading-[1.08] md:leading-[1.05] tracking-[-0.01em] text-foreground-50 font-light">
              {cat.name}.
            </h1>
            <p className="text-[15px] md:text-[14px] text-foreground-100/90 mt-3 max-w-lg">
              {intros[slug] || "Field-tested essentials."}
            </p>
          </div>
        </section>

        <section className="bg-white text-neutral-900 py-12 md:py-16">
          <div className="w-full px-5 md:px-10">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500">{list.length} products</p>
              <Link to="/shop" className="text-[12px] text-neutral-700 hover:text-primary-600 border-b border-neutral-300 pb-1 cursor-pointer">
                View all shop
              </Link>
            </div>

            {list.length === 0 ? (
              <div className="text-center py-16 text-[13px] text-neutral-500">More {cat.name.toLowerCase()} coming this season.</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-10 gap-y-8 md:gap-y-12 max-w-[1600px] mx-auto" data-product-shop>
                {list.map((p, i) => (
                  <ProductCard key={p.slug} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}